import { FC, memo, PointerEventHandler, useEffect, useState } from 'react';
import { useScratch } from 'react-use';
import { Flex, SxStyleProp } from 'theme-ui';
import { clamp } from 'ramda';
import { roundToStep } from 'utils/math';

export type SliderProps = {
  value: number;
  onChange: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
  tooltip?: string;
  className?: string;
  onPointerUp?: PointerEventHandler;
  onPointerDown?: PointerEventHandler;
  onPointerMove?: PointerEventHandler;
  minDiff?: number;
};

const HANDLE_INNER_WIDTH = 6;
const HANDLE_BORDER_WIDTH = 4;
const TRACK_HEIGHT = 5;

const trackStyles: SxStyleProp = {
  position: 'relative',
  height: `${TRACK_HEIGHT}px`,
  bg: '#DCDDE9',
  cursor: 'pointer',
  userSelect: 'none'
};
const fillerStyles: SxStyleProp = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  bg: 'primary'
};
const handleStyles: SxStyleProp = {
  boxSizing: 'content-box',
  position: 'absolute',
  top: `-${HANDLE_BORDER_WIDTH + (HANDLE_INNER_WIDTH - TRACK_HEIGHT) / 2}px`,
  right: `-${HANDLE_BORDER_WIDTH + HANDLE_INNER_WIDTH / 2}px`,
  width: `${HANDLE_INNER_WIDTH}px`,
  height: `${HANDLE_INNER_WIDTH}px`,
  borderColor: 'white',
  borderStyle: 'solid',
  borderWidth: `${HANDLE_BORDER_WIDTH}px`,
  borderRadius: `${HANDLE_INNER_WIDTH + HANDLE_BORDER_WIDTH / 2}px`,
  boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
  bg: 'primary'
};

const tooltipStyles: SxStyleProp = {
  position: 'absolute',
  top: -56,
  right: 0,
  transform: 'translateX(50%)',
  fontSize: 2,
  fontWeight: 'heading',
  color: 'body',

  userSelect: 'none',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  transition: '100ms ease-in opacity',
  bg: 'grey6',
  opacity: 0.8,
  borderRadius: '4px',
  width: 56,
  height: 32,
  alignItems: 'center',
  justifyContent: 'center',
  '::after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    bottom: '-8px',
    width: 0,
    height: 0,

    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',

    borderTop: '8px solid ',
    borderTopColor: 'grey6',
    zIndex: 5
  }
};

export const Slider: FC<SliderProps> = memo(
  ({
    max = 100,
    min,
    value = 0,
    className,
    onChange,
    tooltip,
    step = 1,
    minDiff = 0,
    ...props
  }) => {
    const [ref, state] = useScratch();
    const width = `${100 * ((value - min) / (max - min))}%`;
    2;
    const [isHover, setHover] = useState<boolean>(false);
    useEffect(() => {
      if (
        state.isScratching &&
        typeof state.x !== 'undefined' &&
        typeof state.dx !== 'undefined' &&
        typeof state.elW !== 'undefined'
      ) {
        const rangedValue = clamp(0, 1, (state.x + state.dx) / state.elW) * (max - min) + min;
        const roundedToStep = roundToStep(step, rangedValue);
        const clampedValue = clamp(min, max, roundedToStep);
        const shiftedValue = clampedValue ? clampedValue - 1 : 0;
        onChange(clampedValue < minDiff ? minDiff : shiftedValue);
      }
    }, [state.x, state.dx, state.elW, max, min, state.isScratching, onChange]);

    const onMouseEnterHandler = () => {
      setHover(true);
    };
    const onMouseLeaveHandler = () => {
      setHover(false);
    };

    return (
      <div
        className={className}
        sx={trackStyles}
        ref={ref}
        onMouseEnter={onMouseEnterHandler}
        onMouseLeave={onMouseLeaveHandler}
        data-test="slider-box"
        {...props}
      >
        <div data-test="slider-control" sx={fillerStyles} style={{ width }}>
          {tooltip && (
            <Flex
              data-test="slider-text"
              sx={isHover || state.isScratching ? tooltipStyles : { display: 'none' }}
            >
              {tooltip}
            </Flex>
          )}
          {(isHover || state.isScratching) && (
            <div sx={handleStyles} className="sliderHandle" draggable={false} />
          )}
        </div>
      </div>
    );
  }
);
